import axios from "axios";
import { apiRest } from "./constants";
import { decodeToken, encodeToken } from "./utils";

/**
 * Cliente axios único y consistente
 * - baseURL = apiRest (tu API REST)
 * - withCredentials = true (cookies)
 */
const apiClient = axios.create({
  baseURL: apiRest,
  withCredentials: true,
});

// Función para normalizar paths: "products" -> "/products"
const normalizePath = (path = "") => {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
};

// Función recursiva para manejar objetos anidados
export function appendFormData(formData, key, value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    // Si es un objeto, convertirlo a JSON
    formData.append(key, JSON.stringify(value));
  } else if (Array.isArray(value)) {
    // Si es un arreglo, recorrer sus elementos
    value.forEach((item, index) => {
      formData.append(`${key}[${index}]`, JSON.stringify(item));
    });
  } else if (value !== null && value !== undefined) {
    // Para valores primitivos o no nulos/indefinidos
    formData.append(key, value);
  }
}

const safeErrorResponse = (error, fallbackMessage = "Network error") => {
  // Mantiene tu patrón de "return error.response.data" pero sin reventar si no existe
  const resData = error?.response?.data;
  if (resData) return resData;

  return {
    error: true,
    message: fallbackMessage,
  };
};

/**
 * apiBasic
 * - Envia "data" tal cual (o FormData si hay files)
 * - Soporta delete con body (axios lo mete en config.data)
 * - Devuelve response.data como antes
 */
export const apiBasic = async (data, path, method = "post") => {
  try {
    const url = normalizePath(path);

    let payload = data;
    const config = {};

    if (data?.files) {
      config.headers = { "Content-Type": "multipart/form-data" };
      const dataForm = new FormData();

      // Recorrer el objeto principal (incluye files como key, luego lo pisamos abajo)
      for (const [key, value] of Object.entries(data)) {
        appendFormData(dataForm, key, value);
      }

      // Recorrer imágenes
      if (Array.isArray(data.files)) {
        if (data.files.length > 0)
          data.files.forEach((image) => dataForm.append("files", image));
        else dataForm.append("files", data.files);
      } else {
        // si viene un solo file
        dataForm.append("files", data.files);
      }

      payload = dataForm;
    }

    const lower = String(method || "post").toLowerCase();

    // En axios, delete/ get no llevan "body" como segundo argumento; el body va en config.data
    if (lower === "get") {
      const response = await apiClient.get(url, { params: payload, ...config });
      return response.data;
    }

    if (lower === "delete") {
      const response = await apiClient.delete(url, {
        data: payload,
        ...config,
      });
      return response.data;
    }

    const response = await apiClient.request({
      url,
      method: lower,
      data: payload,
      ...config,
    });

    return response.data;
  } catch (error) {
    console.error(`🚀 ~ apiBasic ${path} ~ error:`, error);
    return safeErrorResponse(error, `apiBasic error (${path})`);
  }
};

/**
 * Variante para endpoints paginados que conservan el body habitual y exponen
 * los metadatos de paginación mediante cabeceras HTTP.
 */
export const apiBasicWithPagination = async (data, path, method = "post") => {
  try {
    const response = await apiClient.request({
      url: normalizePath(path),
      method: String(method || "post").toLowerCase(),
      data,
    });

    const getHeaderNumber = (name, fallback) => {
      const value = Number(response.headers?.[name]);
      return Number.isFinite(value) && value > 0 ? value : fallback;
    };

    return {
      ...response.data,
      pagination: {
        totalCount: getHeaderNumber("x-total-count", 0),
        totalPages: getHeaderNumber("x-total-pages", 1),
        currentPage: getHeaderNumber("x-current-page", 1),
        pageSize: getHeaderNumber("x-page-size", 20),
      },
    };
  } catch (error) {
    console.error(`🚀 ~ apiBasicWithPagination ${path} ~ error:`, error);
    return safeErrorResponse(error, `apiBasicWithPagination error (${path})`);
  }
};

/**
 * apiToken
 * - Encripta data en un token (JWT + AES payload)
 * - Envía { token } o FormData
 * - Devuelve response.data y añade data decodificada (ojo: response.data.data es el token)
 */
export const apiToken = async (data, path, method = "post") => {
  try {
    const url = normalizePath(path);
    const token = encodeToken(data);

    let payload;
    const config = {};

    if (data?.files) {
      config.headers = { "Content-Type": "multipart/form-data" };
      const dataForm = new FormData();

      for (const [key, value] of Object.entries(data)) {
        appendFormData(dataForm, key, value);
      }

      if (Array.isArray(data.files)) {
        data.files.forEach((image) => dataForm.append("files", image));
      } else if (data.files) {
        dataForm.append("files", data.files);
      }

      payload = dataForm;
    } else {
      payload = { token };
    }

    const lower = String(method || "post").toLowerCase();

    if (lower === "delete") {
      const response = await apiClient.delete(url, {
        data: payload,
        ...config,
      });
      const raw = response.data;

      // Si el backend devuelve token en raw.data, lo decodificamos
      const decoded = raw?.data ? decodeToken(raw.data) : false;
      return { ...raw, data: decoded };
    }

    const response = await apiClient.request({
      url,
      method: lower,
      data: payload,
      ...config,
    });

    const raw = response.data;

    // 🔧 FIX: decodeToken necesita un token string, normalmente viene en raw.data
    const decoded = raw?.data ? decodeToken(raw.data) : false;

    return { ...raw, data: decoded };
  } catch (error) {
    console.log("🚀 ~ apiToken " + path + " ~ error:", error);
    return safeErrorResponse(error, `apiToken error (${path})`);
  }
};

/**
 * apiTokenNotDecode
 * - Igual que tu versión: devuelve response axios completo (no .data)
 * - Se usa en logIn para luego hacer decodeToken(response.data.data)
 */
export const apiTokenNotDecode = async (data, path) => {
  try {
    const url = normalizePath(path);
    const token = encodeToken(data);

    const response = await apiClient.post(url, { token });
    return response;
  } catch (error) {
    console.log("🚀 ~ apiTokenNotDecode " + path + " ~ error:", error);
    return safeErrorResponse(error, `apiTokenNotDecode error (${path})`);
  }
};

/**
 * apiGet
 * - Ahora consistente: usa apiClient + normalizePath
 * - params por defecto es ""
 */
export const apiGet = async (path, params = "") => {
  try {
    const url = normalizePath(path);
    const response = await apiClient.get(url, { params });
    return response.data;
  } catch (error) {
    console.log("🚀 ~ apiGet " + path + " ~ error:", error);
    return safeErrorResponse(error, `apiGet error (${path})`);
  }
};

/**
 * logout
 * - Se llama contra el host raíz (/logout), no contra apiRest completo
 */
export const logout = async () => {
  const baseApi = apiRest.replace(/^(https?:\/\/[^/]+).*$/, "$1");

  try {
    const response = await axios.delete(`${baseApi}/logout`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error(`🚀 ~ logout ~ error:`, error);
    return safeErrorResponse(error, "logout error");
  }
};

/**
 * getCorporationInfo
 * - Se llama contra el host raíz (/get-corporation-info)
 */
export const getCorporationInfo = async () => {
  const baseApi = apiRest.replace(/^(https?:\/\/[^/]+).*$/, "$1");

  try {
    const response = await axios.get(`${baseApi}/get-corporation-info`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error(`🚀 ~ getCorporationInfo ~ error:`, error);
    return safeErrorResponse(error, "getCorporationInfo error");
  }
};
